import Controller from '@ember/controller';
import {
  action,
  computed,
  get,
  getProperties,
  set,
  setProperties,
} from '@ember/object';
import { alias, map } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { task } from 'ember-concurrency';
import moment from 'moment';
import dateUtils from '../../../../lib/date-utils';

export default Controller.extend({
  coachingService: service(),
  formatDate: service('formatDateService'),
  router: service(),
  store: service(),

  changeset: alias('model.changeset'),

  error: null,
  showValidationErrors: false,

  reset() {
    setProperties(this, {
      error: null,
      showValidationErrors: false,
    });
  },

  frequencyNumberOptions: [
    { label: '1', val: 1 },
    { label: '2', val: 2 },
    { label: '3', val: 3 },
    { label: '4', val: 4 },
  ],

  frequencyTimespanOptions: [
    { label: 'Week', val: 'week' },
    { label: 'Month', val: 'month' },
  ],

  firstReportEndDate: computed(
    'changeset.{startDate,frequencyNumber,frequencyTimespan}',
    function () {
      let changeset = this.changeset;
      let { startDate, frequencyNumber, frequencyTimespan } = getProperties(
        changeset,
        'startDate',
        'frequencyNumber',
        'frequencyTimespan',
      );
      if (
        isEmpty(startDate) ||
        isEmpty(frequencyNumber) ||
        isEmpty(frequencyTimespan)
      ) {
        return null;
      }
      return this.coachingService.reportEndDate({
        startDate,
        frequencyNumber,
        frequencyTimespan,
      });
    },
  ),

  // [{ date, dateString, dateFormatted, reportCount }]
  goalDateItems: computed(
    'changeset.{startDate,frequencyNumber,frequencyTimespan}',
    function () {
      let changeset = this.changeset;
      let { coachingService, formatDate } = getProperties(
        this,
        'coachingService',
        'formatDate',
      );
      let { startDate, frequencyNumber, frequencyTimespan } = getProperties(
        changeset,
        'startDate',
        'frequencyNumber',
        'frequencyTimespan',
      );
      let maxDate = moment(startDate).add(2, 'years');
      let currentDate = startDate;
      let reportCount = 1;
      let items = [];
      while (currentDate < maxDate) {
        currentDate = coachingService.reportEndDate({
          startDate: currentDate,
          frequencyNumber,
          frequencyTimespan,
        });

        items.push({
          date: currentDate,
          dateString: moment(currentDate).format('YYYY-MM-DD'),
          dateFormatted: formatDate.format(currentDate, { fourYear: true }),
          reportCount: reportCount,
        });

        currentDate = moment(currentDate).add(1, 'day').toDate();
        reportCount += 1;
      }
      return items;
    },
  ),

  goalDateOptions: map('goalDateItems', function (goalDateItem) {
    return {
      val: goalDateItem.dateString,
      label: goalDateItem.dateFormatted,
    };
  }),

  reportingPeriodsToGoalDate: computed('changeset.goalDateString', function () {
    let goalDateString = get(this, 'changeset.goalDateString');
    let selectedGoalDateItem = this.goalDateItems.find(
      (x) => x.dateString === goalDateString,
    );
    if (selectedGoalDateItem) {
      return selectedGoalDateItem.reportCount;
    } else {
      return null;
    }
  }),

  convertFieldGoals() {
    get(this, 'changeset.sections').forEach((section) => {
      get(section, 'fields').forEach((field) => {
        if (field.overallGoal !== undefined) {
          if (isEmpty(field.overallGoal)) {
            delete field.overallGoal;
          } else {
            set(field, 'overallGoal', Number(field.overallGoal));
          }
        }
        // right now just doing intervalGoal.low
        // later we'll have to handle intervalGoal.high
        let intervalGoal = get(field, 'intervalGoal.low');
        if (intervalGoal == null) {
          delete field.intervalGoal;
        } else {
          set(field, 'intervalGoal.low', Number(intervalGoal));
        }
      });
    });
  },

  save: task(function* () {
    this.convertFieldGoals();
    let changeset = this.changeset;
    yield changeset.validate();
    if (get(changeset, 'isValid')) {
      try {
        set(changeset, 'filledOut', true);
        yield changeset.save();
        this.router.transitionTo(
          'coaching.individuals.individual',
          get(changeset, 'id'),
        );
      } catch (error) {
        set(this, 'error', error);
      }
    }
  }).drop(),

  handleGoalDateStringChanged: action(function (newVal) {
    if (!isEmpty(newVal)) {
      set(this, 'changeset.goalDate', dateUtils.deserializeDateOnly(newVal));
    }
  }),
});
